import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { StyleSheet, css } from 'aphrodite';
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
  profilePicture: Yup.mixed()
    .required('Profile picture is required')
    .test('fileFormat', 'Unsupported file format', (value) => {
      if (value) {
        const supportedFormats = ['image/jpg', 'image/jpeg', 'image/png'];
        return supportedFormats.includes(value.type);
      }
      return false;
    })
    .test('fileSize', 'File size is too large', (value) => {
      if (value) {
        return value.size <= 2 * 1024 * 1024; // 2MB
      }
      return false;
    })
});

const optionalValidationSchema = Yup.object().shape({
  profilePicture: Yup.mixed()
    .test('fileFormat', 'Unsupported file format', (value) => {
      if (value) {
        const supportedFormats = ['image/jpg', 'image/jpeg', 'image/png'];
        return supportedFormats.includes(value.type);
      }
      return false;
    })
    .test('fileSize', 'File size is too large', (value) => {
      if (value) {
        return value.size <= 2 * 1024 * 1024; // 2MB
      }
      return false;
    })
});

const ProfilePicture = ({ onComplete, optional }) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isValid, setIsValid] = useState(false);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    try {
      if (!optional) {
        // Required: will throw if no file or validation fails
        await validationSchema.validate({ profilePicture: selectedFile });
      } else {
        // Optional: validate only if a file is provided
        if (selectedFile) {
          await optionalValidationSchema.validate({
            profilePicture: selectedFile
          });
        }
      }
      setFile(selectedFile);
      setError('');
      setSuccess('File uploaded successfully');
      setIsValid(true);
    } catch (err) {
      setError(err.message);
      setSuccess('');
      setIsValid(false);
    }
  };

  const handleSubmit = () => {
    if (isValid) {
      onComplete({ profilePicture: file }, isValid);
    }
  };

  return (
    <motion.div
      className={css(styles.container)}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className={css(styles.title)}>Profile Picture</h2>
      <input
        type='file'
        accept='.jpg,.jpeg,.png'
        onChange={handleFileChange}
        className={css(styles.fileInput)}
      />
      {error && <p className={css(styles.error)}>{error}</p>}
      {success && <p className={css(styles.success)}>{success}</p>}
      <motion.button
        className={css(styles.button, !isValid && styles.disabledButton)}
        onClick={handleSubmit}
        whileHover={isValid ? { scale: 1.05 } : {}}
        whileTap={isValid ? { scale: 0.95 } : {}}
        disabled={!isValid}
      >
        {!optional ? 'Next' : 'Update Picture'}
      </motion.button>
    </motion.div>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: '2rem'
  },
  title: {
    fontSize: '1.5rem',
    marginBottom: '1rem',
    color: '#2783F7'
  },
  fileInput: {
    marginBottom: '1rem'
  },
  error: {
    color: 'red',
    marginBottom: '1rem'
  },
  success: {
    color: 'green',
    marginBottom: '1rem'
  },
  button: {
    backgroundColor: '#2783F7',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    fontSize: '1rem',
    borderRadius: '5px',
    cursor: 'pointer',
    marginTop: '1rem'
  },
  disabledButton: {
    backgroundColor: '#a0aec0',
    cursor: 'not-allowed'
  }
});

export default ProfilePicture;
